import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FeedbackCategory } from '../proto/generated/user-feedback'

export const MAX_ATTACHMENTS = 5

export type FeedbackMediaAttachment = {
    type: 'screenshot' | 'video'
    url: string
}

export type FeedbackCaptureKind = 'screenshot' | 'video'

export interface FeedbackStateType {
    category: FeedbackCategory
    description: string
    attachments: FeedbackMediaAttachment[]
    /**
     * Set by the form, consumed by FeedbackCaptureLayer. The layer lives outside the sidebar
     * so it survives the panel being hidden while the screen is captured.
     */
    captureRequest: FeedbackCaptureKind | null
    isCapturing: boolean
    captureError: string | null
}

const initialState: FeedbackStateType = {
    category: FeedbackCategory.FEEDBACK,
    description: '',
    attachments: [],
    captureRequest: null,
    isCapturing: false,
    captureError: null
}

function revokeUrl(url: string) {
    try { URL.revokeObjectURL(url) } catch { /* ignore */ }
}

const feedbackSlice = createSlice({
    name: 'feedback',
    initialState,
    reducers: {
        setCategory(state, action: PayloadAction<FeedbackCategory>) {
            state.category = action.payload
        },
        setDescription(state, action: PayloadAction<string>) {
            state.description = action.payload
        },
        addAttachment(state, action: PayloadAction<FeedbackMediaAttachment>) {
            if (state.attachments.length < MAX_ATTACHMENTS) {
                state.attachments.push(action.payload)
            } else {
                revokeUrl(action.payload.url)
            }
        },
        removeAttachment(state, action: PayloadAction<number>) {
            const [removed] = state.attachments.splice(action.payload, 1)
            if (removed) revokeUrl(removed.url)
        },
        requestCapture(state, action: PayloadAction<FeedbackCaptureKind>) {
            state.captureRequest = action.payload
        },
        clearCaptureRequest(state) {
            state.captureRequest = null
        },
        setCapturing(state, action: PayloadAction<boolean>) {
            state.isCapturing = action.payload
        },
        setCaptureError(state, action: PayloadAction<string | null>) {
            state.captureError = action.payload
        },
        /** Clears the draft after a successful send; attachment object URLs are released. */
        resetFeedbackDraft(state) {
            state.description = ''
            state.attachments.forEach((a) => revokeUrl(a.url))
            state.attachments = []
            state.captureError = null
        }
    }
})

export const {
    setCategory,
    setDescription,
    addAttachment,
    removeAttachment,
    requestCapture,
    clearCaptureRequest,
    setCapturing,
    setCaptureError,
    resetFeedbackDraft
} = feedbackSlice.actions

export default feedbackSlice.reducer
